import React, { useRef, useEffect } from "react";
import { Container, Sprite } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import LogoController from "./LogoController";
import { safeInit } from "../../../../utilities";

// Device-specific multipliers and offsets
const DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      deviceMultiplier: 1.35,
      yOffsetMultiplier: 1.7,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      deviceMultiplier: 1.0,
      yOffsetMultiplier: 1.75,
    },
  },
};

const LogoPortrait = ({ toolbar, renderMode = "static" }) => {
  const { device, orientation, symbolScale, symbolHeight } = useDimensions();
  const logoControllerRef = useRef(null);
  const containerRef = useRef(null);

  const texture = PIXI.Assets.get(ASSET_NAME.logo_portrait);

  const getDeviceConfig = () => {
    return (
      DEVICE_CONFIG[device]?.[orientation] || {
        deviceMultiplier: 1.0,
        yOffsetMultiplier: 1.0,
      }
    );
  };

  const getScaledDimensions = (config) => {
    if (!texture) return { scaledWidth: 0, scaledHeight: 0 };
    const nativeWidth = texture.width;
    const nativeHeight = texture.height;
    const scaledWidth = nativeWidth * config.deviceMultiplier * symbolScale;
    const scaledHeight = nativeHeight * config.deviceMultiplier * symbolScale;
    return { scaledWidth, scaledHeight };
  };

  useEffect(() => {
    // Only create controller if spine mode is enabled
    if (
      renderMode === "spine" &&
      containerRef.current &&
      !logoControllerRef.current &&
      texture
    ) {
      const deviceConfig = getDeviceConfig();
      const { scaledWidth, scaledHeight } = getScaledDimensions(deviceConfig);

      safeInit(() => {
        logoControllerRef.current = new LogoController(
          containerRef.current,
          scaledWidth,
          scaledHeight,
          deviceConfig
        );
        const shouldBeVisible =
          device !== PixiSettings.DEVICE.DESKTOP &&
          orientation === PixiSettings.ORIENTATION.PORTRAIT;
        logoControllerRef.current.setVisible(shouldBeVisible);
      });
    }

    return () => {
      if (logoControllerRef.current) {
        logoControllerRef.current.cleanup();
        logoControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texture, renderMode]);

  useEffect(() => {
    if (logoControllerRef.current && texture) {
      const deviceConfig = getDeviceConfig();
      const { scaledWidth, scaledHeight } = getScaledDimensions(deviceConfig);

      // Only update dimensions if spine is initialized
      if (logoControllerRef.current.isInitialized) {
        logoControllerRef.current.updateDimensions(
          scaledWidth,
          scaledHeight,
          deviceConfig
        );
      }

      const shouldBeVisible =
        renderMode === "spine" &&
        device !== PixiSettings.DEVICE.DESKTOP &&
        orientation === PixiSettings.ORIENTATION.PORTRAIT;
      logoControllerRef.current.setVisible(shouldBeVisible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode, symbolScale, symbolHeight, device, orientation, texture]);

  if (renderMode === "static") {
    if (device === PixiSettings.DEVICE.DESKTOP) {
      return null;
    }
    if (orientation === PixiSettings.ORIENTATION.LANDSCAPE) {
      return null;
    }
    if (!texture) {
      return null;
    }
  }

  const deviceConfig = getDeviceConfig();

  const getLogoPosition = () => {
    const x = toolbar.width / 2;
    const y = symbolHeight * deviceConfig.yOffsetMultiplier;
    return { x, y };
  };

  const logoPosition = getLogoPosition();
  const { scaledWidth, scaledHeight } = getScaledDimensions(deviceConfig);

  return (
    <Container
      ref={containerRef}
      x={logoPosition.x}
      y={logoPosition.y}
      name="logo_portrait"
    >
      {renderMode === "static" && texture && (
        <Sprite
          zIndex={5}
          texture={texture}
          width={scaledWidth}
          height={scaledHeight}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default LogoPortrait;
